import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ICreateAccount, ICreateAccountRaw } from '../shared/account/create-account.helper';
import { IPageablePayload, IRangePayload, ISortablePayload } from '../shared/common.types';
import { HttpUtilsService } from '../shared/http-utils/http-utils.service';
import { DEFAULT_METADATA, ItemResponseWrapper, ListResponseWrapper } from '../shared/http.types';
import { Advertiser, AdvertiserTypes } from '../shared/types/advertiser.types';

interface IGetAdvertisersPayload extends IPageablePayload {
  search?: string;
}

interface IGetWlAdvertisersPayload extends IPageablePayload, ISortablePayload, IRangePayload {
  whiteLabelHash: string;
  type?: AdvertiserTypes;
}

@Injectable({ providedIn: 'root' })
export class AdvertiserHttpService {
  private readonly httpClient: HttpClient;
  private readonly httpUtilsService: HttpUtilsService;

  constructor() {
    this.httpClient = inject(HttpClient);
    this.httpUtilsService = inject(HttpUtilsService);
  }

  public getAdvertiser(id: string): Observable<ItemResponseWrapper<Advertiser.IDetails>> {
    return this.httpClient.get<ItemResponseWrapper<Advertiser.IDetails>>(`${environment.backendBaseUrl}/advertisers/${id}`);
  }

  public getAdvertisers(payload: IGetAdvertisersPayload = { page: 1, pageSize: 10 }): Observable<ICreateAccount[]> {
    return this.httpClient.get<ListResponseWrapper<ICreateAccount>>(`${environment.backendBaseUrl}/advertisers`, { params: { ...payload } })
      .pipe(
        map(this.httpUtilsService.listWrapperResponseMapper),
      );
  }

  public getWlAdvertiser(id: string): Observable<ItemResponseWrapper<Advertiser.IDetails>> {
    return this.httpClient.get<ItemResponseWrapper<Advertiser.IDetails>>(`${environment.backendBaseUrl}/advertisers/${id}/whiteLabel`);
  }

  public getWlAdvertisers({ whiteLabelHash, ...queryPayload }: IGetWlAdvertisersPayload): Observable<ListResponseWrapper<Advertiser.IStatsForWhiteLabel>> {
    return this.httpClient
      .get<ListResponseWrapper<Advertiser.IStatsForWhiteLabel>>(
        `${environment.backendBaseUrl}/advertisers/whiteLabel/${whiteLabelHash}`,
        {
          params: {
            ...queryPayload,
            type: queryPayload.type ?? '',
          },
        },
      )
      .pipe(
        catchError(() => of({ data: [], meta: { ...DEFAULT_METADATA } } as ListResponseWrapper<Advertiser.IStatsForWhiteLabel>)),
      );
  }

  public updateWlAdvertiser(id: string, payload: Partial<Advertiser.IDetails>): Observable<any> {
    return this.httpClient.put(`${environment.backendBaseUrl}/advertisers/${id}/whiteLabel`, payload);
  }

  public createAdvertiser(value: ICreateAccountRaw): Observable<ICreateAccountRaw> {
    return this.httpClient.post<ItemResponseWrapper<ICreateAccountRaw>>(environment.backendBaseUrl + '/advertisers', value)
      .pipe(
        map(this.httpUtilsService.entityWrapperResponseMapper),
        catchError(this.httpUtilsService.simplifyErrorResponse$),
      );
  }

}
